import { eachHourOfInterval, format } from 'date-fns';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { 
  Button, 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Switch, 
  TextField, 
  InputAdornment,
  FormHelperText,
  Paper,
  Stack,
  Divider,
  Grid,
  useTheme,
  alpha,
  FormControlLabel
} from '@mui/material';
import { trackAnalytics } from 'src/features/analytics/analytics.slice';
import {
  getUserBookingAvailability,
  updateUserBookingAvailability,
} from 'src/features/user/user.slice';
import { compareChanges } from 'src/libs/utils';
import {
  bookingAvailabilityMapper,
  bookingAvailabilitySerializer,
  getBookingAvailabilityEmptyState,
  weekDays,
} from 'src/middleware/Photograph.middleware';
import { useAppDispatch, useAppSelector } from 'src/store';
import { availabilitySchema } from 'src/utils/formSchemas';
import ErrorPopup from '../ErrorPopup';
import SuccessPopup from '../SuccessPopup';
import { delayTime } from '../ToastMessage';
import subscriptionPopup from '../Subscription/SubscriptionPopup';

const START_WORKING_HOUR = 0;
const END_WORKING_HOUR = 23;

const generateTimeInterval = (
  startHour = START_WORKING_HOUR,
  endHour = END_WORKING_HOUR
) => {
  const START_DATE = new Date();
  START_DATE.setHours(startHour, 0, 0, 0);
  START_DATE.setMinutes(0, 0, 0);
  
  const END_DATE = new Date();
  END_DATE.setHours(endHour, 0, 0, 0);
  END_DATE.setMinutes(0, 0, 0);

  const workingHoursInterval = eachHourOfInterval({
    start: START_DATE,
    end: END_DATE,
  });

  return workingHoursInterval;
};

const WeekDayItem = ({
  dayName,
  values,
  errors,
  touched,
  disabled,
  handleChange,
  workingHours,
  setFieldValue,
}) => {
  const theme = useTheme();
  const strftime = require('strftime');

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: 3, 
        mb: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: theme.shadows[2],
        }
      }}
    >
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
            {dayName}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={values[dayName].enabled}
                onChange={(event) => {
                  setFieldValue(`[${dayName}][enabled]`, event.target.checked);
                }}
                disabled={disabled}
                color="primary"
              />
            }
            label={values[dayName].enabled ? "Active" : "Inactive"}
          />
        </Box>

        {values[dayName].enabled && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={
                (values[dayName].enabled && !values[dayName].start_time) ||
                parseInt(values[dayName].start_time) >= parseInt(values[dayName].end_time)
              }>
                <InputLabel>Start Time *</InputLabel>
                <Select
                  label="Start Time *"
                  name={`${dayName}.start_time`}
                  value={values[dayName].start_time}
                  onChange={handleChange}
                  disabled={disabled}
                >
                  <MenuItem value="">-</MenuItem>
                  {workingHours.map((date: Date) => (
                    <MenuItem key={format(date, "HH:mm")} value={format(date, "HH:mm")}>
                      {format(date, "hh:mm aa")}
                    </MenuItem>
                  ))}
                </Select>
                {errors[dayName]?.start_time && (
                  <FormHelperText>{errors[dayName].start_time}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                error={values[dayName].enabled && !values[dayName].end_time}
              >
                <InputLabel>Stop Time *</InputLabel>
                <Select
                  label="Stop Time *"
                  name={`[${dayName}][end_time]`}
                  value={values[dayName].end_time}
                  onChange={handleChange}
                  disabled={disabled}
                >
                  <MenuItem value="">-</MenuItem>
                  {workingHours.map((date: Date) => (
                    <MenuItem key={strftime("%H:%M", date)} value={strftime("%H:%M", date)}>
                      {strftime("%I:%M %p", date)}
                    </MenuItem>
                  ))}
                </Select>
                {errors[dayName]?.end_time && (
                  <FormHelperText>{errors[dayName].end_time}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Stack>
    </Paper>
  );
};

const BookingAvailabilityForm = () => {
  const [successMessage, setSuccessMessage] = useState("");
  const workingHours = generateTimeInterval();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { bookingAvailability, userError, userLoading } = useAppSelector((state) => state.user);
  const { subscriptionFeatures } = useAppSelector((state) => state.auth);
  let initialValues = getBookingAvailabilityEmptyState();

  if (bookingAvailability) {
    const currentAvailability = bookingAvailabilitySerializer(
      bookingAvailability?.booking_availabilities
    );
    initialValues = { 
      ...initialValues, 
      ...currentAvailability, 
      lead_time: bookingAvailability.lead_time, 
      appointment_buffer: bookingAvailability.appointment_buffer || 0 
    };
  }

  const onSubmit = (values) => {
    let payload = compareChanges(initialValues, values);
    const lead_time = values.lead_time;
    const appointment_buffer = values.appointment_buffer;
    delete(values.lead_time);
    delete(values.appointment_buffer);
    const bookingAvailabilityData = bookingAvailabilityMapper(values);
    dispatch(updateUserBookingAvailability({
      ...bookingAvailabilityData, 
      lead_time: lead_time, 
      appointment_buffer: appointment_buffer
    }))
    .unwrap()
    .then((response) => {
      dispatch(trackAnalytics({
        action_name: "booking_availability_saved", 
        payload: {...payload, description: "user updated booking availability form"}
      }));
      setSuccessMessage("Successfully Updated");
      setTimeout(() => {
        setSuccessMessage("");
      }, delayTime);
    });
  };

  useEffect(() => {
    dispatch(getUserBookingAvailability());
  }, []);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        Booking Availability
      </Typography>
      
      {userError && <ErrorPopup message={userError}/>}
      {successMessage && <SuccessPopup message={successMessage}/>}

      <Formik
        initialValues={initialValues}
        validationSchema={availabilitySchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          handleSubmit,
          setFieldValue,
          handleChange,
          resetForm,
          values,
          touched,
          errors,
        }) => (
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                mb: 4,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              }}
            >
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
                Scheduling Parameters
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Lead time before soonest availability"
                    placeholder="1 hour - 24 hours"
                    id="lead_time"
                    name="lead_time"
                    type="number"
                    value={values.lead_time}
                    onChange={handleChange}
                    disabled={userLoading}
                    error={touched.lead_time && !!errors.lead_time}
                    helperText={touched.lead_time && errors.lead_time}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Hours</InputAdornment>,
                      inputProps: { min: 1, max: 24 }
                    }}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Buffer time between appointments"
                    placeholder="0 - 120 minutes"
                    id="appointment_buffer"
                    name="appointment_buffer"
                    type="number"
                    value={values.appointment_buffer}
                    onChange={handleChange}
                    disabled={userLoading}
                    error={touched.appointment_buffer && !!errors.appointment_buffer}
                    helperText={touched.appointment_buffer && errors.appointment_buffer}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Minutes</InputAdornment>,
                      inputProps: { min: 0, max: 120 }
                    }}
                    required
                  />
                </Grid>
              </Grid>
            </Paper>

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
              Weekly Schedule
            </Typography>

            <Stack spacing={0}>
              {weekDays.map((day) => (
                <WeekDayItem
                  key={day}
                  values={values}
                  touched={touched}
                  dayName={day}
                  errors={errors}
                  disabled={userLoading}
                  handleChange={handleChange}
                  workingHours={workingHours}
                  setFieldValue={setFieldValue}
                />
              ))}
            </Stack>

            <Divider sx={{ my: 4 }} />
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button 
                variant="contained" 
                type="submit" 
                disabled={userLoading}
                size="large"
                sx={{ minWidth: 120 }}
              >
                Save
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => resetForm()}
                size="large"
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>
            </Stack>
          </Box>
        )}
      </Formik>
    </Box>
  );
};

export default BookingAvailabilityForm;
